import { useState } from 'react';
import { Download } from 'lucide-react';
import Button from '../ui/Button';
import { downloadJsonFile } from '../../lib/downloadFile';
import { getApiErrorMessage } from '../../lib/apiError';
import { cn } from '../../lib/utils';

interface ExportJsonButtonProps {
  label?: string;
  filename: string | (() => string);
  collect: () => Promise<unknown> | unknown;
  disabled?: boolean;
  className?: string;
}

/**
 * Outline control that builds a JSON blob and saves it. Used on admin Security so a
 * snapshot of the current window can be shared without copy-paste from the tables.
 */
export default function ExportJsonButton({
  label = 'Download',
  filename,
  collect,
  disabled,
  className,
}: ExportJsonButtonProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onClick = async () => {
    setError(null);
    setBusy(true);
    try {
      const data = await collect();
      const name = typeof filename === 'function' ? filename() : filename;
      downloadJsonFile(name, data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to build the download'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className={cn('inline-flex flex-col items-end gap-1', className)}>
      <Button type="button" variant="outline" size="sm" onClick={() => void onClick()} disabled={disabled || busy}>
        <Download className={cn('mr-2 h-4 w-4', busy && 'animate-pulse')} aria-hidden />
        {busy ? 'Preparing…' : label}
      </Button>
      {error && (
        <span className="max-w-xs text-right text-xs text-[rgb(var(--danger))]" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}
